import React, { ReactElement, useMemo } from 'react';
import { TFunction } from 'i18next';
import { toDate, toTime } from 'utils/helpers/datetime';
import Table, {
    createDetailLinkTh,
    createLinkByType,
    createTd,
    createTh,
    ShowMoreUrlInterface,
} from 'components/tables/Table';
import { useTranslation } from 'utils/localization';
import {
    ColumnColor,
    ColumnHeadInterface,
    ColumnSize,
    ColumnValueInterface,
    ColumnValueWeight,
    FiltersInterface,
} from 'components/interfaces/GeneralInterface';
import { stringifyScore } from 'utils/helpers/browser/match';
import {
    EntityReferenceApiEnum,
    MatchApiInterface,
    PaginatorInterface,
} from 'submodules/api_middleware';
import { RoleEnum } from 'types/types';
import { getLinkByRole } from 'components/tables/Column';
import { getRoute } from 'constants/routes';
import styles from './MatchesBlock.module.scss';

interface Props {
    person_id?: number;
    entries: MatchApiInterface[];
    title?: string | null;
    mainTitle?: string | ReactElement;
    showMoreUrl?: ShowMoreUrlInterface;
    showRoles?: boolean;
    paginator?: PaginatorInterface;
    noEntriesSubText?: string;
    filterConfig?: FiltersInterface;
}

export const getMatchTdColor = (state: number): ColumnColor =>
    state === 1
        ? ColumnColor.GREEN
        : state >= 3
        ? undefined
        : ColumnColor.ORANGE;

export const getMatchTdLabel = (
    t: TFunction,
    state: number,
    result: string
): string => {
    switch (state) {
        case 1:
            return t('MemberProfile.willBePlayed');
        case 2:
            return t('MemberProfile.planned');
        case 3:
            return result;
        case 4:
            return t('MemberProfile.contumacy');
        case 5:
            return t('MemberProfile.postponed');
        case 6:
            return t('match.not_started');
        default:
            return '-';
    }
};

const MatchesBlock = ({
    entries,
    mainTitle,
    title,
    paginator,
    showMoreUrl,
    person_id,
    showRoles,
    noEntriesSubText,
    filterConfig,
}: Props): ReactElement => {
    const { t } = useTranslation();
    if (!entries) return null;

    const columns: ColumnHeadInterface[] = useMemo<ColumnHeadInterface[]>(
        () => [
            createDetailLinkTh((id) =>
                getRoute('/matches/{0}', [id as string])
            ),
            createTh(
                t,
                'ClubProfile.segmentRound',
                ColumnSize.S,
                undefined,
                true
            ),
            createTh(t, 'match.homeAway'),
            createTh(t, 'match.dateTime', ColumnSize.M),
            createTh(t, 'ClubMatches.placeCompetition', ColumnSize.L),
            ...(showRoles
                ? [createTh(t, 'RefereeProfile.role', ColumnSize.S)]
                : []),
            createTh(t, 'general.result', ColumnSize.S),
        ],
        []
    );
    const data: ColumnValueInterface[][] = entries.map(
        ({
            id,
            home_team,
            guest_team,
            playing_at,
            pitch,
            competition,
            state,
            persons_roles,
            score,
            season,
            round,
        }) => [
            createTd(id),
            createTd([t('match.season-' + season) as string, '' + round]),
            createTd(
                [
                    createLinkByType(
                        t,
                        {
                            team_id: home_team.id,
                            club_id: home_team.club.id,
                            competition_id: competition.id,
                            name: home_team.small_name,
                        },
                        EntityReferenceApiEnum.club_competition
                    ),
                    createLinkByType(
                        t,
                        {
                            team_id: guest_team.id,
                            club_id: guest_team.club.id,
                            competition_id: competition.id,
                            name: guest_team.small_name,
                        },
                        EntityReferenceApiEnum.club_competition
                    ),
                ],
                ColumnValueWeight.BOLD_FIRST
            ),
            createTd([toDate(playing_at), toTime(playing_at)]),
            createTd(
                [
                    pitch.name,
                    createLinkByType(
                        t,
                        competition,
                        EntityReferenceApiEnum.competition,
                        undefined,
                        undefined,
                        true
                    ),
                ],
                ColumnValueWeight.BOLD_FIRST
            ),
            ...(showRoles
                ? [
                      createTd(
                          persons_roles?.reduce((result: any, role) => {
                              // todo parsedRole odstranit po spravnej implementacii roli na BE
                              const parsedRole = Object.values(RoleEnum).find(
                                  (item) => role.role.startsWith(item)
                              );
                              return role.person_id === person_id
                                  ? [
                                        ...result,
                                        {
                                            name: parsedRole,
                                            ...getLinkByRole(
                                                parsedRole,
                                                person_id,
                                                t
                                            ),
                                        },
                                    ]
                                  : result;
                          }, [])
                      ),
                  ]
                : []),
            createTd(
                getMatchTdLabel(t, state, stringifyScore(score)),
                ColumnValueWeight.BOLD,
                getMatchTdColor(state)
            ),
        ]
    );

    const cardTitle = title
        ? title
        : title !== null
        ? t('match.matchesActivePlanned')
        : undefined;

    return (
        <div className={styles.root}>
            <Table
                filterConfig={filterConfig}
                columns={columns}
                data={data}
                noEntriesSubText={
                    noEntriesSubText || t('MemberProfile.noMatchesTableMember')
                }
                mainCardTitle={mainTitle}
                cardTitle={cardTitle}
                showMoreUrl={showMoreUrl}
                paginator={paginator}
            />
        </div>
    );
};

export default MatchesBlock;
